
import { useState } from 'react';

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    location: '',
    systemSize: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('');

    try {
      const formDataToSend = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        formDataToSend.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/submit/solar-consultation', {
        method: 'POST',
        body: formDataToSend
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          phone: '',
          email: '',
          location: '',
          systemSize: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: "ri-phone-line",
      title: "Call Us",
      details: ["7496038344"],
      color: "emerald",
      action: "tel:7496038344"
    },
    {
      icon: "ri-mail-line",
      title: "Email Us",
      details: ["info@bharatsolarindia.com"],
      color: "blue",
      action: "mailto:info@bharatsolarindia.com"
    },
    {
      icon: "ri-map-pin-line",
      title: "Service Area",
      details: ["All across Haryana", "PM Surya Ghar Yojana 2025"],
      color: "amber",
      action: "#"
    },
    {
      icon: "ri-time-line",
      title: "Working Hours",
      details: ["Mon - Sat: 10:00 AM - 5:00 PM", "Sunday: Closed"],
      color: "purple",
      action: "#"
    }
  ];

  const colorClasses: Record<string, string> = {
    emerald: "from-emerald-500 to-emerald-600",
    blue: "from-blue-500 to-blue-600",
    amber: "from-amber-500 to-amber-600",
    purple: "from-purple-500 to-purple-600"
  };

  return (
    <section id="contact" className="py-20 bg-gradient-to-br from-gray-50 to-white">
      <div className="container mx-auto px-6">
        <div className="text-center mb-16">
          <div className="inline-flex items-center bg-emerald-100 text-emerald-700 px-4 py-2 rounded-full text-sm font-medium mb-4">
            <i className="ri-customer-service-2-line mr-2"></i>
            Get In Touch
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Start Your Solar Journey
            <span className="block text-transparent bg-gradient-to-r from-emerald-600 to-amber-500 bg-clip-text">
              Today
            </span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Ready to reduce your electricity bills and contribute to a cleaner environment? 
            Contact our solar experts for a free consultation and personalized quote.
          </p>
        </div>

        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div className="bg-white rounded-3xl shadow-xl p-8">
              <div className="mb-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  Get Your Free Solar Quote
                </h3>
                <p className="text-gray-600">
                  Fill out the form below and our experts will contact you within 24 hours with a customized solar solution.
                </p>
              </div>

              <form id="solar-consultation" onSubmit={handleSubmit} className="space-y-6" data-readdy-form>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors text-sm"
                      placeholder="Enter your full name"
                    />
                  </div>
                  <div>
                    <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors text-sm"
                      placeholder="Enter your phone number"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors text-sm"
                    placeholder="Enter your email address"
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="location" className="block text-sm font-semibold text-gray-700 mb-2">
                      Location *
                    </label>
                    <input
                      type="text"
                      id="location"
                      name="location"
                      value={formData.location}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors text-sm"
                      placeholder="City, State"
                    />
                  </div>
                  <div>
                    <label htmlFor="systemSize" className="block text-sm font-semibold text-gray-700 mb-2">
                      Preferred System Size
                    </label>
                    <select
                      id="systemSize"
                      name="systemSize"
                      value={formData.systemSize}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors text-sm pr-8"
                    >
                      <option value="">Select system size</option>
                      <option value="2kw">2 kW - Small Home</option>
                      <option value="3kw">3 kW - Medium Home</option>
                      <option value="5kw">5 kW - Large Home</option>
                      <option value="custom">Custom Size</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">
                    Additional Requirements
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    rows={4}
                    maxLength={500}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors text-sm resize-none"
                    placeholder="Tell us about your specific requirements, roof type, current electricity bill, etc. (Max 500 characters)"
                  ></textarea>
                  <div className="text-right text-xs text-gray-500 mt-1">
                    {formData.message.length}/500 characters
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-gradient-to-r from-emerald-600 to-amber-500 text-white py-4 px-8 rounded-xl font-semibold hover:from-emerald-700 hover:to-amber-600 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap transform hover:scale-105 shadow-lg"
                >
                  {isSubmitting ? (
                    <span className="flex items-center justify-center">
                      <i className="ri-loader-4-line animate-spin mr-2"></i>
                      Submitting...
                    </span>
                  ) : (
                    <span className="flex items-center justify-center">
                      <i className="ri-send-plane-line mr-2"></i>
                      Get Free Consultation
                    </span>
                  )}
                </button>

                {submitStatus === 'success' && (
                  <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-emerald-700">
                    <div className="flex items-center">
                      <i className="ri-check-circle-line mr-2"></i>
                      Thank you! We'll contact you within 24 hours.
                    </div>
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700">
                    <div className="flex items-center">
                      <i className="ri-error-warning-line mr-2"></i>
                      Something went wrong. Please try again or call us directly.
                    </div>
                  </div>
                )}
              </form>
            </div>

            {/* Contact Information */}
            <div className="space-y-8">
              <div className="grid gap-6">
                {contactInfo.map((info, index) => (
                  <a
                    key={index}
                    href={info.action}
                    className="group bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 cursor-pointer"
                  >
                    <div className="flex items-start space-x-4">
                      <div className={`w-14 h-14 bg-gradient-to-r ${colorClasses[info.color]} rounded-2xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300`}>
                        <i className={`${info.icon} text-white text-xl`}></i>
                      </div>
                      <div className="flex-1">
                        <h4 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-emerald-600 transition-colors">
                          {info.title}
                        </h4>
                        <div className="space-y-1">
                          {info.details.map((detail, detailIndex) => {
                            const parts = detail.split(/(Closed)/i);
                            return (
                              <div key={detailIndex} className="text-gray-600 text-sm">
                                {parts.map((part, idx) =>
                                  /closed/i.test(part) ? (
                                    <span key={idx} className="text-red-600 font-medium">{part}</span>
                                  ) : (
                                    <span key={idx}>{part}</span>
                                  )
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                      <div className="flex-shrink-0">
                        <i className="ri-arrow-right-line text-gray-400 group-hover:text-emerald-600 group-hover:translate-x-1 transition-all duration-300"></i>
                      </div>
                    </div>
                  </a>
                ))}
              </div>

              {/* Quick Contact */}
              <div className="bg-gradient-to-r from-emerald-600 to-amber-500 rounded-3xl p-8 text-white">
                <h3 className="text-2xl font-bold mb-4">Need Immediate Assistance?</h3>
                <p className="text-emerald-100 mb-6">
                  Our solar experts are available to answer your questions and provide instant quotes over the phone.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <a
                    href="tel:7496038344"
                    className="flex items-center justify-center bg-white text-emerald-600 px-6 py-3 rounded-full font-semibold hover:bg-gray-50 transition-all duration-300 whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-phone-line mr-2"></i>
                    Call: 7496038344
                  </a>
                  {/* <a
                    href="tel:7496038344"
                    className="flex items-center justify-center border-2 border-white text-white px-6 py-3 rounded-full font-semibold hover:bg-white hover:text-emerald-600 transition-all duration-300 whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-phone-line mr-2"></i>
                    Call: 7496038344
                  </a> */}
                </div>
              </div>

              {/* Trust Indicators */}
              <div className="bg-white rounded-2xl p-6 shadow-lg">
                <h4 className="font-bold text-gray-900 mb-4">Why Choose Us?</h4>
                <div className="space-y-3">
                  <div className="flex items-center">
                    <i className="ri-shield-check-line text-emerald-500 mr-3"></i>
                    <span className="text-gray-700 text-sm">Authorized PM Surya Ghar Yojana Partner</span>
                  </div>
                  <div className="flex items-center">
                    <i className="ri-award-line text-amber-500 mr-3"></i>
                    <span className="text-gray-700 text-sm">10,000+ Successful Installations</span>
                  </div>
                  {/* <div className="flex items-center">
                    <i className="ri-customer-service-2-line text-blue-500 mr-3"></i>
                    <span className="text-gray-700 text-sm">24/7 Customer Support</span>
                  </div> */}
                  <div className="flex items-center">
                    <i className="ri-money-rupee-circle-line text-purple-500 mr-3"></i>
                    <span className="text-gray-700 text-sm">Guaranteed Subsidy Processing</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
